// src/components/layouts/app/header/mobile/MobileHeader.tsx
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Search, Heart, Menu, Home, Briefcase, ShoppingBag,
  ShoppingCart, Bell
} from "lucide-react";
import { getFavoritesCount } from "../../../../../utils/favorites";
import { getCartItemsCount } from "../../../../../utils/storage";
import MobileSideMenu from "./MobileSideMenu";
import MobileFullScreenSearch from "./MobileFullScreenSearch";

interface MobileHeaderProps {
  onFavoritesClick: () => void;
}

const useNotificationCount = () => {
  const [count] = useState(3);
  return count;
};

const MobileHeader: React.FC<MobileHeaderProps> = ({ onFavoritesClick }) => {
  const location = useLocation();
  const [favoriteCount, setFavoriteCount] = useState(0);
  const [cartCount, setCartCount] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const notificationCount = useNotificationCount();

  const displayFavCount = 2;
  const displayCartCount = 5;

  const refreshFavoritesCount = () => setFavoriteCount(getFavoritesCount());
  const refreshCartCount = () => setCartCount(getCartItemsCount());

  useEffect(() => {
    refreshFavoritesCount();
    refreshCartCount();
    window.addEventListener('favoritesUpdated', refreshFavoritesCount);
    window.addEventListener('cartUpdated', refreshCartCount);
    return () => {
      window.removeEventListener('favoritesUpdated', refreshFavoritesCount);
      window.removeEventListener('cartUpdated', refreshCartCount);
    };
  }, []);

  useEffect(() => {
    const handleOpenMenu = () => setIsMenuOpen(true);
    window.addEventListener('openMobileMenu', handleOpenMenu);
    return () => window.removeEventListener('openMobileMenu', handleOpenMenu);
  }, []);

  useEffect(() => {
    if (isMenuOpen || isSearchOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen, isSearchOpen]);

  const handleCartClick = () => {
    window.dispatchEvent(new CustomEvent("openCart"));
  };

  const handleNotificationClick = () => {
    console.log("Notifications");
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md shadow-gray-200/80">
        {/* Première barre */}
        <div className="flex items-center justify-between px-2 sm:px-4 py-2.5 bg-white">
          <Link to="/" className="flex items-center active:opacity-70 transition-opacity">
            <img src="/assets/logo/v2/logonissirec.png" alt="NISSI GROUP INTL" className="h-10" />
          </Link>
          <div className="flex items-center gap-3">
            {/* Recherche */}
            <button
              onClick={() => setIsSearchOpen(true)}
              className="p-2 rounded-full bg-gray-200 text-black hover:bg-gray-300 active:bg-gray-400 transition-colors"
            >
              <Search size={20} strokeWidth={1.8} />
            </button>

            {/* Favoris */}
            <button
              onClick={onFavoritesClick}
              className="relative p-2 rounded-full bg-gray-200 text-black hover:bg-gray-300 active:bg-gray-400 transition-colors"
            >
              <Heart size={20} strokeWidth={1.8} />
              {displayFavCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[11px] font-bold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center shadow-sm ring-2 ring-white">
                  {displayFavCount}
                </span>
              )}
            </button>

            {/* Menu */}
            <button
              onClick={() => setIsMenuOpen(true)}
              className="p-2 rounded-full bg-gray-200 text-black hover:bg-gray-300 active:bg-gray-400 transition-colors"
            >
              <Menu size={20} strokeWidth={1.8} />
            </button>
          </div>
        </div>

        {/* Deuxième barre - navigation */}
        <div className="flex items-center justify-around  py-1.5 bg-white ">
          {/* Accueil */}
          <Link
            to="/"
            className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-colors ${isActive("/")
                ? "text-primary"
                : "text-black hover:bg-gray-100"
              }`}
          >
            <Home size={20} strokeWidth={isActive("/") ? 2 : 1.8} />
            <span className="text-[11px] font-medium">Accueil</span>
          </Link>

          {/* Services */}
          <Link
            to="/services"
            className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-colors ${isActive("/services")
                ? "text-primary"
                : "text-black hover:bg-gray-100"
              }`}
          >
            <Briefcase size={20} strokeWidth={isActive("/services") ? 2 : 1.8} />
            <span className="text-[11px] font-medium">Services</span>
          </Link>

          {/* Boutique */}
          <Link
            to="/boutique"
            className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-colors ${isActive("/boutique")
                ? "text-primary"
                : "text-black hover:bg-gray-100"
              }`}
          >
            <ShoppingBag size={20} strokeWidth={isActive("/boutique") ? 2 : 1.8} />
            <span className="text-[11px] font-medium">Boutique</span>
          </Link>

          {/* Panier */}
          <button
            onClick={handleCartClick}
            className="relative flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-colors text-black hover:bg-gray-100"
          >
            <ShoppingCart size={20} strokeWidth={1.8} />
            <span className="text-[11px] font-medium">Panier</span>
            {displayCartCount > 0 && (
              <span className="absolute -top-1 -right-0 bg-primary text-white text-[11px] font-bold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center shadow-sm ring-2 ring-white">
                {displayCartCount}
              </span>
            )}
          </button>

          {/* Notifications */}
          <button
            onClick={handleNotificationClick}
            className="relative flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-colors text-black hover:bg-gray-100"
          >
            <Bell size={20} strokeWidth={1.8} />
            <span className="text-[11px] font-medium">Notif</span>
            {notificationCount > 0 && (
              <span className="absolute -top-1 -right-0 bg-red-600 text-white text-[11px] font-bold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center shadow-sm ring-2 ring-white">
                {notificationCount}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="h-[calc(57px+45px)]" />

      <MobileSideMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
      <MobileFullScreenSearch isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
};

export default MobileHeader;